import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { usePublicForm } from '../hooks/usePublicForm';
import { useSubmitForm } from '../hooks/useSubmitForm';
import { FormResponseView } from '../components/FormRenderer/FormResponseView';
import { buildSubmissionSchema, mapSubmissionValues } from '../schemas';

export default function PublicFormSubmissionPage() {
  const { formId } = useParams<{ formId: string }>();
  const { form, isLoading, isError, errorCode } = usePublicForm(formId ?? '');
  const submitForm = useSubmitForm(formId ?? '');
  const [submitted, setSubmitted] = useState(false);

  const sortedFields = form?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  const schema = buildSubmissionSchema(sortedFields, form?.emailRequired ?? false);

  const rhfForm = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', fields: {} },
  });

  const handleSubmit = rhfForm.handleSubmit(async (data) => {
    try {
      await submitForm.mutateAsync({
        email: data.email || null,
        isAnonymous: !data.email,
        values: mapSubmissionValues(data.fields as Record<string, unknown>),
      });
      setSubmitted(true);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <FormResponseView
        testIdPrefix="forms-public"
        isLoading={isLoading}
        isError={isError}
        errorCode={errorCode}
        form={form}
        submitted={submitted}
        rhfForm={rhfForm}
        onSubmit={handleSubmit}
        isSubmitting={submitForm.isPending}
      />
    </FeatureErrorBoundary>
  );
}
